// Core
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Styles
import "./styles.scss";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  getAllStaffsRequest_ADMIN,
  deleteStaffRequest_ADMIN,
  markAsActiveInactiveStaffRequest_ADMIN,
} from "../../../../Redux/Slices/Admin/adminSlice";

// Custom layout
import MainLayout from "Layouts/Main";

// Assets
import Colors from "../../../../Assets/SassConstants/colors.scss";
import Filter from "../../../../Assets/Svg/Admin/AssessmentBacklog/filter.svg";
import More from "../../../../Assets/Svg/Admin/AssessmentBacklog/more.svg";
import ViewIcon from "../../../../Assets/Svg/Admin/AssessmentBacklog/view.svg";
import EditIcon from "../../../../Assets/Svg/Admin/AssessmentBacklog/edit.svg";
import DeleteIcon from "../../../../Assets/Svg/Admin/AssessmentBacklog/delete.svg";
import InactiveIcon from "../../../../Assets/Svg/Admin/AssessmentBacklog/inactive.svg";
import InputBox from "Components/InputBox";

// Utils
import { notifySuccess } from "Services/Utils/toastNotification";

// Custom component
import Pagination from "../../../../Components/Pagination";
import ConfirmationModal from "Components/ConfirmationModal";
import NormalTable from "Components/NormalTable";

const StaffManagement = () => {
  // Initialize useNavigate
  const navigate = useNavigate();

  //Initialize redux
  const dispatch = useDispatch();

  //==================== List screen start ====================
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffDetails, setSelectedStaffDetails] = useState({});
  const [activeRow, setActiveRow] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({
    sortBy: null,
    sortOrder: 0,
  });
  const staffManagementHeader = [
    { label: "S.No", isSort: false, key: "sno" },
    { label: "Assessor Name", isSort: true, key: "user_name" },
    { label: "Employee ID", isSort: true, key: "user_code" },
    { label: "Mobile Number", isSort: true, key: "mobile" },
    // { label: "Open Requests", isSort: true, key: "openRequests" },
    { label: "Status", isSort: false, key: "status" },
    { label: "", isSort: false, key: "" },
  ];
  const menuRef = useRef(null);
  const isLoading = useSelector((state) => state.admin.isLoading);

  const handlePageChange = async (page) => {
    await setCurrentPage(page);
    await getAllStaffsRequest_ADMIN_(page);
  };

  const handleMenuToggle = (rowIndex) => {
    setActiveRow(activeRow === rowIndex ? null : rowIndex);
  };

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setActiveRow(null);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  }

  useEffect(() => {
    getAllStaffsRequest_ADMIN_(currentPage);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sort, search]);

  //To handle the index
  const getDisplayedIndex = (currentPage, index) => {
    // Calculate the starting index for the current page
    const startingIndex = (currentPage - 1) * 8 + 1;
    return startingIndex + index;
  };

  //To get all staff details
  const getAllStaffsRequest_ADMIN_ = (page) => {
    dispatch(
      getAllStaffsRequest_ADMIN({
        page: page,
        search,
        sortBy: sort.sortOrder === 1 ? "ASC" : "DESC",
        orderBy: sort.sortBy,
        limit: 8,
        callBack: getAllStaffsRequestCB_ADMIN,
      })
    );
  };

  //To handle the response of getAllStaffsRequest_ADMIN
  const getAllStaffsRequestCB_ADMIN = (data) => {
    setTotalPages(data?.pageMeta?.totalPages);
    setStaffList(data?.data_list);
  };
  //==================== List screen end ====================

  //==================== Staff details start ====================
  //Delete staff
  const handleStaffDelete = () => {
    dispatch(
      deleteStaffRequest_ADMIN({
        userId: selectedStaffDetails?.user_id,
        callBack: deleteStaffRequestCB_ADMIN,
      })
    );
  };

  //Handle callback of deleteStaffRequest_ADMIN
  const deleteStaffRequestCB_ADMIN = () => {
    setIsConfirmationModalOpen(false);
    notifySuccess(`${selectedStaffDetails?.user_name} deleted successfully!`);
    setSelectedStaffDetails({});
    if (staffList?.length === 1) {
      setCurrentPage(currentPage - 1);
      getAllStaffsRequest_ADMIN_(currentPage - 1);
    } else {
      getAllStaffsRequest_ADMIN_(currentPage);
    }
  };

  //Mark as active inactive
  const markAsActiveInactive = (row) => {
    dispatch(
      markAsActiveInactiveStaffRequest_ADMIN({
        userId: row?.user_id,
        callBack: () => {
          // Fetch updated staff list
          getAllStaffsRequest_ADMIN_(currentPage);
          // Show success notification
          notifySuccess("Status Updated Successfully for " + row?.user_name);
        },
      })
    );
  };
  //==================== Staff details end ====================

  return (
    <MainLayout style={{ backgroundColor: Colors.grey_12 }}>
      <div className="staff-management-admin-container">
        {/* Heading and Button Section */}
        <div className="staff-management-admin-row staff-management-admin-header-row">
          <h1 className="staff-management-admin-heading">Staff Management</h1>
          <div className="d-flex flex-row align-items-center me-3 gap-2">
            <InputBox
              name="search"
              isSearch={true}
              placeholder="Search"
              value={search}
              onChange={(e) => handleSearch(e)}
              inputStyle={{ width: "277px" }}
            />
            <button
              onClick={() => {
                navigate("/staff-management/add-update-staff", {
                  state: { selectedStaffDetails: {} },
                });
              }}
              className="staff-management-admin-add-button"
            >
              Add Staff
            </button>
          </div>
        </div>

        <div className="backlog-table">
          <NormalTable
            loading={isLoading === 'loading'}
            headers={staffManagementHeader}
            sort={sort}
            setSort={setSort}
            currentPage={currentPage}
            noDataColspan={9}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            customStyle={{
              customTableHeaderContainer: "customAdminStaffTableHeader",
              customHeaderTitle: "customAdminStaffHeaderStartTitle",
              customTable: "customAdminStaffTable",
              customTableContainer: "customAdminStaffTableContainer",
            }}
            noDataLabel={staffList?.length === 0}
          >
            {staffList?.map((row, index) => {
              const isLastRow = index === staffList.length - 1;
              const isSecondLastRow = index === staffList.length - 2;
              return (
                <tr key={index} style={{ position: "relative" }}>
                  <td>
                    <div className="TableDivisionStart ms-5 ps-2 mt-2">
                      {getDisplayedIndex(currentPage, index)}
                    </div>
                  </td>
                  <td>
                    <div className="TableDivisionStart ms-2 mt-2">
                      {row.user_name ?? "-"}
                    </div>
                  </td>
                  <td>
                    <div className="TableDivisionStart ms-2 mt-2">
                      {row.user_code ?? "-"}
                    </div>
                  </td>
                  <td>
                    <div className="TableDivisionStart ms-2 mt-2">
                      {row.country_code}&nbsp;{row.mobile ?? "-"}
                    </div>
                  </td>
                  {/* <td>
                    <div className="TableDivisionStart ms-2 mt-2">
                      {row.openRequests ?? "-"}
                    </div>
                  </td> */}
                  <td>
                    <div className="TableDivisionStart ms-2 mt-2">
                      {row.status === 2 ? "Active" : "Inactive"}
                    </div>
                  </td>

                  <td>
                    <div
                      className="TableDivisionStart ms-5 mt-2"
                      onClick={() => handleMenuToggle(index)}
                      style={{ cursor: "pointer" }}
                    >
                      <img src={More} alt="More" />
                      {activeRow === index && (
                        <div
                          className="admin-menu-options"
                          ref={menuRef}
                          style={{
                            ...(isLastRow || isSecondLastRow
                              ? { top: "-100px" }
                              : {}),
                          }}
                        >
                          <div
                            onClick={() => {
                              navigate("/view-staff", {
                                state: { selectedStaffDetails: row },
                              });
                            }}
                            className="admin-menu-option"
                          >
                            <img src={ViewIcon} alt="View Icon" />
                            View
                          </div>
                          <div
                            onClick={() => {
                              navigate("/staff-management/add-update-staff", {
                                state: { selectedStaffDetails: row },
                              });
                            }}
                            className="admin-menu-option"
                          >
                            <img src={EditIcon} alt="Edit Icon" />
                            Edit
                          </div>
                          <div
                            onClick={() => {
                              setSelectedStaffDetails(row);
                              setIsConfirmationModalOpen(true);
                            }}
                            className="admin-menu-option"
                          >
                            <img src={DeleteIcon} alt="Delete Icon" />
                            Detele
                          </div>
                          <div
                            onClick={() => {
                              markAsActiveInactive(row);
                            }}
                            className="admin-menu-option"
                          >
                            <img src={InactiveIcon} alt="Inactive Icon" />
                            {row.status === 2
                              ? " Mark as Inactive "
                              : " Mark as Active "}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </NormalTable>
          {/* {staffList?.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )} */}
        </div>
      </div>

      {isConfirmationModalOpen && (
        <ConfirmationModal
          message={"Are you sure you want to delete this staff?"}
          onCancel={() => {
            setSelectedStaffDetails({});
            setIsConfirmationModalOpen(false);
          }}
          onConfirm={handleStaffDelete}
        />
      )}
    </MainLayout>
  );
};

export default StaffManagement;
